import { newUuid } from '../api/httpClient'
import { browserStorages, type KeyValueStorage } from '../persistence/storage'

/**
 * Кто на этом конце: устройство и вкладка браузера (RP-CA4). Сервер привязывает к этой паре вход в статистику кассира: чужая вкладка
 * или другое устройство тот же пропуск не используют.
 *
 * Устройство — постоянный идентификатор в хранилище устройства; вкладка — идентификатор в хранилище вкладки. «Дублировать вкладку»
 * копирует хранилище вкладки, поэтому две живые вкладки могли бы получить один идентификатор: новая вкладка спрашивает остальных
 * через `BroadcastChannel`, и копия берёт себе новый идентификатор.
 */
export const CLIENT_IDENTITY_HEADERS = { device: 'X-Client-Device', tab: 'X-Client-Tab' } as const

const DEVICE_KEY = 'krp.client.device'
const TAB_KEY = 'krp.client.tab'
const CHANNEL_NAME = 'krp.client.tab'
const MAX_FACT_LENGTH = 200

interface TabMessage { kind: 'claim' | 'taken'; tabId: string; nonce: string }
interface ChannelLike {
  postMessage(message: unknown): void
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void
  close(): void
}

export interface ClientIdentity {
  readonly deviceId: string
  /** Идентификатор вкладки сейчас: меняется один раз, если вкладка оказалась копией другой. */
  tabId(): string
  /** Заголовки для каждого запроса к серверу. */
  headers(): Record<string, string>
  /** Описание устройства для сервера: всё, что браузер сообщает о себе; значения короткие и без персональных данных человека. */
  describeDevice(): Record<string, string>
  dispose(): void
}

export interface ClientIdentityOptions {
  device?: KeyValueStorage
  tab?: KeyValueStorage
  newId?: () => string
  /** `null` — без обнаружения копий вкладки (нет `BroadcastChannel`). */
  channel?: ChannelLike | null
}

const clip = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim().slice(0, MAX_FACT_LENGTH) : typeof value === 'number' && Number.isFinite(value) ? String(value) : null

/** Что браузер сообщает об устройстве. Каждое значение ограничено; чего браузер не знает — не передаётся. */
export function describeBrowserDevice(): Record<string, string> {
  const facts: Record<string, string> = {}
  const put = (key: string, value: unknown) => { const text = clip(value); if (text !== null) facts[key] = text }
  const nav = (globalThis as { navigator?: Navigator & { deviceMemory?: number; userAgentData?: { platform?: string; mobile?: boolean; brands?: { brand: string; version: string }[] } } }).navigator
  if (nav !== undefined) {
    put('userAgent', nav.userAgent)
    put('platform', nav.userAgentData?.platform ?? nav.platform)
    if (nav.userAgentData?.mobile !== undefined) put('mobile', nav.userAgentData.mobile ? 'да' : 'нет')
    if (nav.userAgentData?.brands !== undefined) put('browser', nav.userAgentData.brands.map((item) => `${item.brand} ${item.version}`).join(', '))
    put('language', nav.language)
    put('cores', nav.hardwareConcurrency)
    put('memoryGb', nav.deviceMemory)
    put('touchPoints', nav.maxTouchPoints)
  }
  const screen = (globalThis as { screen?: Screen }).screen
  if (screen !== undefined) put('screen', `${screen.width}x${screen.height}@${(globalThis as { devicePixelRatio?: number }).devicePixelRatio ?? 1}`)
  try { put('timeZone', Intl.DateTimeFormat().resolvedOptions().timeZone) } catch { /* часовой пояс не сообщается */ }
  return facts
}

function browserChannel(): ChannelLike | null {
  try {
    return typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL_NAME) : null
  } catch {
    return null
  }
}

export function createClientIdentity(options: ClientIdentityOptions = {}): ClientIdentity {
  const storages = options.device === undefined || options.tab === undefined ? browserStorages() : null
  const device = options.device ?? storages?.device
  const tab = options.tab ?? storages?.session
  const newId = options.newId ?? newUuid
  const nonce = newId()

  const stored = device?.get(DEVICE_KEY) ?? null
  const deviceId = stored ?? newId()
  if (stored === null) device?.set(DEVICE_KEY, deviceId)

  let currentTab = tab?.get(TAB_KEY) ?? null
  const inherited = currentTab !== null
  if (currentTab === null) { currentTab = newId(); tab?.set(TAB_KEY, currentTab) }

  const channel = options.channel === undefined ? browserChannel() : options.channel
  // Страница, которую закрывают или перезагружают, ещё может получить «claim» от своей же новой копии: если она ответит «taken», вкладка
  // после F5 возьмёт новый идентификатор и сама окажется «чужой» для собственной работы. Уходящая страница не отвечает.
  let leaving = false
  const onHide = () => { leaving = true }
  const onShow = () => { leaving = false }
  const lifecycle = (globalThis as { addEventListener?: (type: string, listener: () => void) => void; removeEventListener?: (type: string, listener: () => void) => void })
  lifecycle.addEventListener?.('pagehide', onHide)
  lifecycle.addEventListener?.('pageshow', onShow)
  channel?.addEventListener('message', (event) => {
    const message = event.data as Partial<TabMessage> | null
    if (leaving || message === null || typeof message !== 'object' || message.tabId !== currentTab) return
    if (message.kind === 'claim' && typeof message.nonce === 'string' && message.nonce !== nonce) channel?.postMessage({ kind: 'taken', tabId: message.tabId, nonce: message.nonce } satisfies TabMessage)
    // Идентификатор уже держит другая живая вкладка: это копия, берём свой.
    else if (message.kind === 'taken' && message.nonce === nonce) { currentTab = newId(); tab?.set(TAB_KEY, currentTab) }
  })
  if (inherited && currentTab !== null) channel?.postMessage({ kind: 'claim', tabId: currentTab, nonce } satisfies TabMessage)

  return {
    deviceId,
    tabId: () => currentTab ?? '',
    headers: () => ({ [CLIENT_IDENTITY_HEADERS.device]: deviceId, [CLIENT_IDENTITY_HEADERS.tab]: currentTab ?? '' }),
    describeDevice: describeBrowserDevice,
    dispose: () => {
      lifecycle.removeEventListener?.('pagehide', onHide)
      lifecycle.removeEventListener?.('pageshow', onShow)
      channel?.close()
    },
  }
}

let shared: ClientIdentity | null = null

/** Устройство и вкладка этой страницы: одна пара на страницу, создаётся при первом обращении. */
export function getClientIdentity(): ClientIdentity {
  shared ??= createClientIdentity()
  return shared
}
